import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Component from '@glimmer/component';
import { action, set } from '@ember/object';
import { inject } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { guidFor } from '@ember/object/internals';
import { task, timeout } from 'ember-concurrency';
import { debug } from '@ember/debug';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

var TEMPLATE = precompileTemplate("{{#unless @custom_field.organization_custom_field.disabled}}\n  {{! This component owns no element of its own — everything it renders is\n      yielded into BbField\'s slot — so a consumer\'s attribute has to be\n      forwarded. It could not be, until bb-field.hbs started spreading\n      `...attributes` onto its root grid; before that, a hook here reached no\n      DOM node while reading exactly like a wired one. That is why all 10 of\n      this component\'s callback sites were unaddressable by any test. }}\n  <BbField\n    @label={{@custom_field.organization_custom_field.name}}\n    @classLabel={{@classLabel}}\n    @classText={{@classText}}\n    ...attributes\n  >\n    {{#if (is-equal @custom_field.organization_custom_field.data_type \'date\')}}\n      <BbInputDate\n        @originalDate={{@custom_field.value}}\n        @timeZone={{this.session.me.time_zone}}\n        @setDate={{this.setDateValue}}\n      />\n    {{else if (is-equal @custom_field.organization_custom_field.data_type \'table\')}}\n      <CfTable\n        @custom_field={{@custom_field}}\n        @rangeOption={{this.rangeOption}}\n        @isEdit=\'true\'\n        @applicationController={{@applicationController}}\n      />\n    {{else if (is-equal @custom_field.organization_custom_field.data_type \'dropbox\')}}\n      <BbSelect\n        @prompt={{t \'no selection\'}}\n        @content={{this.customFieldDefinitions}}\n        @action={{this.setValue}}\n        @optionValuePath=\'name\'\n        @optionLabelPath=\'name\'\n        @isSingleArray={{true}}\n        @selection={{@custom_field.value}} data-test-set-value\n      />\n    {{else if (is-equal @custom_field.organization_custom_field.data_type \'number\')}}\n      <Input\n        @type=\'number\'\n        autocomplete=\'off\'\n        @value={{@custom_field.number_value}} data-test-trigger-cf-calculate {{on \'input\' (perform this.triggerCfCalculate)}}\n      />\n    {{else if (is-equal @custom_field.organization_custom_field.data_type \'checkbox\')}}\n      <BbCheckbox @content={{@custom_field.boolean_value}} @setContent={{this.setBooleanValue}} />\n    {{else if (is-equal @custom_field.organization_custom_field.data_type \'currency\')}}\n      <BbInputCurrency\n        @value={{@custom_field.number_value}}\n        @action={{this.setCurrencyValue}}\n        data-test-set-currency-value\n      />\n    {{else if (is-equal @custom_field.organization_custom_field.data_type \'textarea\')}}\n      <Textarea @value={{@custom_field.value}} data-test-trigger-cf-calculate-2 {{on \'input\' (perform this.triggerCfCalculate)}} />\n    {{else if (is-equal @custom_field.organization_custom_field.data_type \'suggestion\')}}\n      {{#if @custom_field.value}}\n        <Input @type=\'text\' @value={{@custom_field.value}} />\n      {{else}}\n        <BbInputSuggestion\n          @applicationController={{@applicationController}}\n          @custom_field={{@custom_field}}\n          @relatedModelType=\'custom-field-definition\'\n          @value={{@custom_field.value}}\n        />\n      {{/if}}\n    {{else if (is-equal @custom_field.organization_custom_field.data_type \'range\')}}\n      {{#if this.rangeOption}}\n        <div {{did-insert this.initRangeValue}}>\n          <BbSlider\n            @value={{@custom_field.number_value}}\n            @changed={{this.setNumberValue}}\n            @min={{this.rangeOption.range.min}}\n            @max={{this.rangeOption.range.max}}\n            @step={{this.rangeOption.step}}\n            @tooltip=\'always\'\n            @mood={{this.rangeOption.mood}}\n            @action={{perform this.triggerCfCalculate}} data-test-trigger-cf-calculate-4\n          />\n        </div>\n      {{/if}}\n    {{else if (is-equal @custom_field.organization_custom_field.data_type \'advancedTextarea\')}}\n      <CfAdvancedTextarea\n        @value={{@custom_field.value}}\n        @onValueChange={{this.setAdvancedTextareaValue}}\n        @applicationController={{@applicationController}}\n        @custom_field={{@custom_field}}\n        @hideExtraTools={{@hideExtraTools}}\n        @ariaLabel={{@custom_field.organization_custom_field.name}} data-test-set-advanced-textarea-value\n      />\n    {{else if (is-equal @custom_field.organization_custom_field.data_type \'calculated\')}}\n      {{#if this._cfCalculating}}\n        <BbSpinner />\n      {{else}}\n        {{@custom_field.number_value}}\n      {{/if}}\n    {{else}}\n      <Input\n        @type=\'text\'\n        @value={{@custom_field.value}} data-test-trigger-cf-calculate-3 {{on \'input\' (perform this.triggerCfCalculate)}}\n      />\n    {{/if}}\n  </BbField>\n{{/unless}}");

var _dec, _class, _descriptor, _descriptor2, _descriptor3;
let EditCustomFieldComponent = (_dec = task(function* () {
  this._cfCalculating = true;
  try {
    yield timeout(50);
    yield this.args.cfCalculateTask?.perform();
  } finally {
    this._cfCalculating = false;
  }
}).restartable(), _class = class EditCustomFieldComponent extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "session", _descriptor, this);
    // ─── Stato locale ─────────────────────────────────────────────────────────
    // Flag per mostrare <BbSpinner> durante il ricalcolo (data_type 'calculated')
    _initializerDefineProperty(this, "_cfCalculating", _descriptor2, this);
    // ─── Debounce ricalcolo ────────────────────────────────────────────────────
    _initializerDefineProperty(this, "triggerCfCalculate", _descriptor3, this);
  }
  // ─── Unique ID (sostituisce this.elementId di Classic) ────────────────────

  get elementId() {
    return guidFor(this);
  }
  // ─── Computed (sostituisce computed(...).readOnly()) ───────────────────────

  get customFieldDefinitions() {
    const data_type = this.args.custom_field?.organization_custom_field?.data_type;
    if (data_type === 'dropbox') {
      return this.args.custom_field?.organization_custom_field?.custom_field_definitions?.map(item => item.name) || [];
    }
    return [];
  }

  // range and table custom fields both store their config as JSON in the
  // first definition's name — one parser, two semantic aliases.
  get _parsedDefinitionOption() {
    const definitions = this.args.custom_field?.organization_custom_field?.custom_field_definitions;
    const name = definitions?.[0]?.name;
    if (name) {
      try {
        return JSON.parse(name);
      } catch (e) {
        debug(e);
      }
    }
    return null;
  }
  get rangeOption() {
    return this._parsedDefinitionOption;
  }
  get tableOption() {
    return this._parsedDefinitionOption;
  }
  // ─── Actions ──────────────────────────────────────────────────────────────

  // Inizializza number_value al valore di start del range se ancora non impostato.
  // Chiamato via did-insert sul wrapper del range slider — evita side effect nel getter.
  initRangeValue() {
    const option = this.rangeOption;
    if (option && this.args.custom_field.number_value == null) {
      set(this.args.custom_field, 'number_value', option.start);
    }
  }

  // Usato da InputCurrency (currency): aggiorna il valore e rilancia il calcolo
  setCurrencyValue(value) {
    set(this.args.custom_field, 'number_value', value);
    this.triggerCfCalculate.perform();
  }

  // Usato da UiSlider (range): aggiorna il valore e rilancia il calcolo
  setNumberValue(value) {
    set(this.args.custom_field, 'number_value', value);
    this.triggerCfCalculate.perform();
  }

  // Usato da InputDate (date): aggiorna il valore e rilancia il calcolo
  setDateValue(value) {
    set(this.args.custom_field, 'value', value);
    this.triggerCfCalculate.perform();
  }

  // Usato da MyCheckbox (checkbox): aggiorna il valore e rilancia il calcolo
  setBooleanValue(value) {
    set(this.args.custom_field, 'boolean_value', value);
    this.triggerCfCalculate.perform();
  }

  // Usato da CfAdvancedTextarea (advancedTextarea): aggiorna solo il valore.
  // Niente triggerCfCalculate: il rich text non entra nelle formule calculated
  // e il ricalcolo a ogni keystroke sarebbe solo costo.
  setAdvancedTextareaValue(value) {
    set(this.args.custom_field, 'value', value);
  }

  // Usato da MySelect (dropbox): aggiorna il valore e rilancia il calcolo
  setValue(newValue) {
    set(this.args.custom_field, 'value', newValue);
    this.triggerCfCalculate.perform();
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "session", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "_cfCalculating", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "triggerCfCalculate", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "initRangeValue", [action], Object.getOwnPropertyDescriptor(_class.prototype, "initRangeValue"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setCurrencyValue", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setCurrencyValue"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setNumberValue", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setNumberValue"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setDateValue", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setDateValue"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setBooleanValue", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setBooleanValue"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setAdvancedTextareaValue", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setAdvancedTextareaValue"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setValue", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setValue"), _class.prototype), _class);
setComponentTemplate(TEMPLATE, EditCustomFieldComponent);

export { EditCustomFieldComponent as default };
//# sourceMappingURL=edit-custom-field.js.map
